import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import styles from "./importDataReviewSmart.module.css"
import reviewStyles from "../importDataReview/importDataReview.module.css"
import MappingResultView from "./importMappingResult"
import Button from "../../../../components/button/button"
import HeaderTitle from "../../../../components/header/headerTitle/headerTitle"
import { uploadGeneric, resolveFieldMapping } from "../../../../services/import"
import { useImportFlow } from "../../../../context/ImportFlowContext"

// Tipos disponíveis para mapeamento manual (chaves iguais às do catálogo do backend).
const TYPE_OPTIONS = [
  { key: "aluno", label: "Alunos" },
  { key: "academico", label: "Acadêmico" },
  { key: "turma", label: "Turmas" },
  { key: "adesao", label: "Adesão" },
  { key: "captacao", label: "Captação" },
  { key: "faturamento", label: "Faturamento" },
  { key: "docente", label: "Docente" }
]

const labelFor = (key) =>
  TYPE_OPTIONS.find((o) => o.key === key)?.label || key

const ImportDataReviewSmart = () => {
  const navigate = useNavigate()
  const { importData, clearImportData } = useImportFlow()

  const [types, setTypes] = useState([])
  const [selected, setSelected] = useState({})
  // Mapeamento por tipo no formato canônico -> coluna do arquivo.
  const [mappings, setMappings] = useState({})
  const [manualType, setManualType] = useState("")
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    if (!importData || !importData.smart) {
      navigate('/app/importar-dados', { replace: true })
      return
    }

    const detected = importData.detection?.detectedTypes || []
    const initialSelected = {}
    const initialMappings = {}
    detected.forEach((d) => {
      initialSelected[d.importType] = true
      initialMappings[d.importType] = { ...(d.fieldMapping || {}) }
    })
    setTypes(detected.map((d) => ({ ...d, manual: false })))
    setSelected(initialSelected)
    setMappings(initialMappings)
  }, [importData, navigate])

  const goBack = useCallback(() => {
    clearImportData()
    navigate('/app/importar-dados')
  }, [clearImportData, navigate])

  const receivedHeaders = useMemo(
    () => importData?.detection?.receivedHeaders || [],
    [importData]
  )
  const sampleRows = useMemo(
    () => importData?.detection?.sampleRows || [],
    [importData]
  )
  const notice = importData?.detection?.notice
  const source = importData?.detection?.source || "ai"
  const rowCount = importData?.detection?.rowCount
  const fileName = importData?.file?.name || importData?.fileName
  // Modo consulta: abre o mapeamento gravado de uma importação concluída, sem edição nem reenvio.
  const readOnly = !!importData?.readOnly

  if (!importData || !importData.smart) return null

  const availableToAdd = TYPE_OPTIONS.filter(
    (o) => !types.some((t) => t.importType === o.key)
  )

  const toggleType = (importType) => {
    setSelected((prev) => ({ ...prev, [importType]: !prev[importType] }))
  }

  // Mapeia um campo do sistema (canonical) para uma coluna do arquivo (fileHeader).
  // Garante que cada coluna do arquivo seja usada por no máximo um campo dentro do tipo.
  const handleHeaderMapping = (importType, canonical, fileHeader) => {
    if (readOnly) return
    setMappings((prev) => {
      const typeMap = { ...(prev[importType] || {}) }
      if (fileHeader) {
        Object.keys(typeMap).forEach((k) => {
          if (typeMap[k] === fileHeader) delete typeMap[k]
        })
        typeMap[canonical] = fileHeader
      } else {
        delete typeMap[canonical]
      }
      return { ...prev, [importType]: typeMap }
    })
  }

  const addManualType = () => {
    if (!manualType) return
    setTypes((prev) => [
      ...prev,
      {
        importType: manualType,
        label: labelFor(manualType),
        confidence: 0,
        manual: true,
        naturalKeySatisfied: false,
        missingKeyFields: [],
        fieldMapping: {},
        warnings: []
      }
    ])
    setSelected((prev) => ({ ...prev, [manualType]: true }))
    setMappings((prev) => ({ ...prev, [manualType]: {} }))
    setManualType("")
  }

  const removeType = (importType) => {
    setTypes((prev) => prev.filter((t) => t.importType !== importType))
    setSelected((prev) => {
      const next = { ...prev }
      delete next[importType]
      return next
    })
    setMappings((prev) => {
      const next = { ...prev }
      delete next[importType]
      return next
    })
  }

  const handleConfirm = async () => {
    const detections = types
      .filter((d) => selected[d.importType])
      .map((d) => ({
        importType: d.importType,
        fieldMapping: mappings[d.importType] || {}
      }))
      .filter((d) => Object.keys(d.fieldMapping).length > 0)

    if (detections.length === 0) {
      toast.error("Selecione ao menos um tipo com colunas mapeadas para importar.")
      return
    }

    try {
      setIsUploading(true)
      // Modo resume (veio de notificação): retoma pelo correlationId, sem re-upload do arquivo.
      // Modo em-fluxo (arquivo em memória): envia o arquivo com as detecções confirmadas.
      const result = importData.correlationId
        ? await resolveFieldMapping({
            correlationId: importData.correlationId,
            detections,
            headerSpec: importData.headerSpec,
            institutionId: importData.institutionId
          })
        : await uploadGeneric({
            file: importData.file,
            institutionId: importData.institutionId,
            detections
          })

      if (result?.isBackground) {
        toast.info(result.message
          || "Importação iniciada! Seu arquivo está sendo mapeado por IA. Avisaremos por notificação quando concluir, e você poderá conferir o mapeamento ao final.")
        goBack()
        return
      }

      const inserted = result?.totalInserted ?? 0
      const updated = result?.totalUpdated ?? 0
      // Mesmo toastId do evento SignalR (import-<correlationId>) para não duplicar o toast.
      toast.success(`Importação concluída! ${inserted} inserido(s), ${updated} atualizado(s).`,
        result?.correlationId ? { toastId: `import-${result.correlationId}` } : undefined)
      goBack()
    } catch (error) {
      const msg = Array.isArray(error)
        ? error.join(' ')
        : (error?.message || (typeof error === 'string' ? error : "Falha ao importar o arquivo."))
      toast.error(msg)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className={reviewStyles.container}>
      <HeaderTitle title={readOnly ? "Importação Inteligente — Mapeamento" : "Importação Inteligente — Revisão"}>
        <div className={reviewStyles.headerActions}>
          <Button
            text="Voltar"
            variation="secondary"
            size="normal"
            onClick={goBack}
            disabled={isUploading}
          />
          {!readOnly && (
            <Button
              text={isUploading ? "Enviando..." : "Enviar Arquivo"}
              variation="primary"
              size="normal"
              onClick={handleConfirm}
              disabled={isUploading || types.length === 0}
            />
          )}
        </div>
      </HeaderTitle>

      {notice && (
        <div className={`${styles.notice} ${source === "none" ? styles.noticeError : styles.noticeWarn}`}>
          {notice}
        </div>
      )}

      {!readOnly && availableToAdd.length > 0 && (
        <div className={styles.addType}>
          <span>Adicionar tipo manualmente:</span>
          <select
            className={styles.select}
            value={manualType}
            onChange={(e) => setManualType(e.target.value)}
          >
            <option value="">Selecione um tipo…</option>
            {availableToAdd.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
          <Button
            text="Adicionar"
            variation="secondary"
            size="normal"
            onClick={addManualType}
            disabled={!manualType}
          />
        </div>
      )}

      {!readOnly && types.length === 0 && (
        <div className={styles.emptyState}>
          <p>Nenhum tipo identificado. Adicione um tipo acima e mapeie as colunas manualmente.</p>
        </div>
      )}

      {types.length > 0 && (
        <MappingResultView
          types={types}
          mappings={mappings}
          receivedHeaders={receivedHeaders}
          sampleRows={sampleRows}
          rowCount={rowCount}
          fileName={fileName}
          editable={!readOnly}
          selected={selected}
          onToggleType={toggleType}
          onMapField={handleHeaderMapping}
          onRemoveType={removeType}
        />
      )}

      <div className={styles.actions}>
        {readOnly ? (
          <Button
            text="Voltar"
            variation="secondary"
            size="big"
            onClick={goBack}
          />
        ) : (
          <>
            <Button
              text="Cancelar"
              variation="secondary"
              size="big"
              onClick={goBack}
              disabled={isUploading}
            />
            <Button
              text={isUploading ? "Importando..." : "Confirmar e importar"}
              variation="primary"
              size="big"
              onClick={handleConfirm}
              disabled={isUploading || types.length === 0}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default ImportDataReviewSmart
